import React from 'react';
import { Link } from 'react-router-dom';

import { usePaperDocument } from '../hooks/usePaperDocument';
import './paper.css';

const SITE = 'https://www.letslivewhimsically.com';
const YOUTUBE = 'https://www.youtube.com/@whimsicallybritt';
const INSTAGRAM = 'https://www.instagram.com/whimsicallybritt';

const STACK = [
  ['Frontend', 'React (Create React App + CRACO), Tailwind, shadcn/ui', 'Vercel'],
  ['Edge', 'Vercel middleware: real 404s, crawler metadata, agent discovery', 'Vercel Edge'],
  ['API', 'FastAPI, async PostgreSQL (databases + asyncpg), slowapi', 'Railway'],
  ['Database & auth', 'PostgreSQL, Supabase Auth', 'Supabase'],
  ['Media', 'R2 object storage behind a CDN subdomain, AVIF image worker', 'Cloudflare'],
  ['Video', 'Encoding, adaptive streaming, in-admin uploads', 'Mux'],
  ['Payments', 'Checkout, Customer Portal, webhooks', 'Stripe Billing'],
  ['Email', 'Transactional mail, newsletter, SMTP fallback', 'Brevo'],
];

const WhimsicallyBrittProject = () => {
  const { theme, toggleTheme } = usePaperDocument('/projects/whimsically-britt');

  return (
    <article className="paper-doc">
      <div className="doc-bar">
        <Link to="/">&larr; Théotime Perrichet</Link>
        <button type="button" onClick={toggleTheme}>
          {theme === 'dark' ? ': Light :' : ': Dark :'}
        </button>
      </div>

      <h1>
        Whimsically Britt: rebuilding the website of a travel content creator
        and travel planner
      </h1>

      <p className="author">Théotime Perrichet</p>
      <p className="doc-affiliation">Freelance full-stack project for Whimsically Britt</p>
      <p className="doc-dateline">Remote · 2026</p>

      <ul className="doc-links">
        <li><a href={SITE}>letslivewhimsically.com</a></li>
        <li><a href={YOUTUBE}>YouTube</a></li>
        <li><a href={INSTAGRAM}>Instagram</a></li>
      </ul>

      <div className="abstract">
        <h2>Abstract</h2>
        <p>
          Whimsically Britt is a solo-travel content creator who left a career
          in finance to travel full time, publishing on YouTube and Instagram
          and selling trip planning to independent travellers who would rather
          not plan alone. I rebuilt her website,{' '}
          <a href={SITE}>letslivewhimsically.com</a>, from scratch as a single
          platform that does three jobs: a travel blog she writes and schedules
          herself, a storefront for her paid planning services, and a
          members-only subscription for exclusive posts. The result is a React
          frontend on Vercel, a FastAPI API on Railway, PostgreSQL and
          authentication on Supabase, media on Cloudflare R2 and Mux, and
          payments on Stripe Billing, all managed through a custom admin so
          that day-to-day running of the site needs no developer.
        </p>
        <p className="keywords">
          <strong>Keywords:</strong> full-stack web development · React ·
          FastAPI · Stripe Billing · headless CMS · edge SEO · freelance
        </p>
      </div>

      <nav aria-label="Table of contents">
        <ol>
          <li><a href="#s1">The client and the brief</a></li>
          <li><a href="#s2">Architecture</a></li>
          <li><a href="#s3">Publishing: a custom admin</a></li>
          <li><a href="#s4">Selling trip planning</a></li>
          <li><a href="#s5">Members-only posts with Stripe</a></li>
          <li><a href="#s6">Media pipeline</a></li>
          <li><a href="#s7">Accounts and security</a></li>
          <li><a href="#s8">Search and discoverability</a></li>
          <li><a href="#s9">Delivery and maintenance</a></li>
        </ol>
      </nav>

      <figure>
        <img
          src="/images/whimsically-britt.webp"
          alt="The Live Whimsically passport-stamp logo: a palm tree and sunset over the sea"
        />
        <figcaption>
          The Live Whimsically passport stamp, the brand mark the redesign is
          built around.
        </figcaption>
      </figure>

      <h2 id="s1">1. The client and the brief</h2>
      <p>
        Britt documents her travels on{' '}
        <a href={YOUTUBE}>YouTube</a> and <a href={INSTAGRAM}>Instagram</a>{' '}
        &mdash; more than forty destinations, including over nine months
        backpacking across Asia &mdash; and turns that experience into a
        business: custom travel guides, full-service trip booking, and one-hour
        planning consultations. Her audience is mostly first-time and solo
        travellers, many of whom find her through her videos.
      </p>
      <p>
        The brief was a full redo rather than a refresh. The website had to
        become the hub that her social channels point to: long-form
        destination guides that rank in search, service pages that turn a
        reader into a client, a way to monetise her most loyal followers, and
        an editing experience simple enough for a creator to run alone. Every
        piece of content, price and legal text had to be editable without
        touching code.
      </p>

      <h2 id="s2">2. Architecture</h2>
      <p>
        The site is a React single-page application served from Vercel&rsquo;s
        edge, talking to a FastAPI backend on Railway. The backend uses
        PostgreSQL on Supabase through an async driver, with SQLAlchemy used for
        query building only, and wires in every third-party service through its
        own module so that each integration can be swapped or disabled
        independently.
      </p>

      <div className="scroll-wrapper">
        <table>
          <caption>Services that make up the platform and where they run.</caption>
          <thead>
            <tr>
              <th scope="col">Layer</th>
              <th scope="col">Role</th>
              <th scope="col">Provider</th>
            </tr>
          </thead>
          <tbody>
            {STACK.map(([layer, role, provider]) => (
              <tr key={layer}>
                <td>{layer}</td>
                <td>{role}</td>
                <td>{provider}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p>
        A background task on the API polls every minute to publish scheduled
        posts, and each publish, unpublish or delete fires a Vercel deploy hook
        so the sitemap is regenerated without waiting for a code push.
      </p>

      <h2 id="s3">3. Publishing: a custom admin</h2>
      <p>
        Rather than bolting a third-party CMS onto the site, I built an admin
        area around how Britt actually works. Posts are written in a
        block-based Editor.js editor with images, embeds, tables, checklists and
        a Giphy search, saved as drafts and scheduled for later. Around the
        editor sit a media library and a video library, categories and tags,
        comment moderation, client reviews, the gallery and visited-countries
        map, the &ldquo;about me&rdquo; page, site information, service prices
        and every legal page (privacy, terms, refunds, travel disclaimer,
        affiliate disclosure, data rights). Admin access is protected by a
        bcrypt-hashed password and TOTP two-factor authentication.
      </p>

      <h2 id="s4">4. Selling trip planning</h2>
      <p>
        Each of the three services &mdash; custom travel guides, full-service
        booking, and travel consultations &mdash; has its own landing page with
        what is included, who it is for, the planning process from first call
        to travelling with support, and a booking call to action, plus a free
        introductory consultation. Prices are stored in the database and edited
        from the admin, never hard-coded, so offers can change in seconds. Call
        to action clicks are tracked as analytics events so the conversion
        funnel from blog post to booking can be measured.
      </p>

      <h2 id="s5">5. Members-only posts with Stripe</h2>
      <p>
        &ldquo;Super Secret Posts&rdquo; is a monthly subscription built on
        Stripe Billing. Stripe Checkout handles sign-up, the Customer Portal
        handles cancellations, card updates and invoices, and the price is
        resolved at runtime from a lookup key so it can change without a
        deploy. Stripe is treated as the source of truth: a local table is a
        read model kept current by signature-verified webhooks, with idempotent
        upserts guarded by event timestamps so that retried or out-of-order
        events cannot corrupt state. Posts flagged subscriber-only return their
        full body only to members; everyone else gets a paywall, and a
        cancelled subscription keeps access until the end of the paid period.
      </p>

      <h2 id="s6">6. Media pipeline</h2>
      <p>
        A travel site lives on its photos. Uploads go through a Cloudflare
        Worker that authenticates the admin token, converts images to AVIF on
        the fly and writes them straight to R2, served through a dedicated CDN
        subdomain; folder listings are cached at the edge. Videos are hosted on
        Mux for encoding and adaptive streaming, uploaded and played back from
        inside the admin and the site.
      </p>

      <h2 id="s7">7. Accounts and security</h2>
      <p>
        Readers can create accounts through Supabase Auth with email
        verification and password recovery, and get a dashboard for their
        profile, likes, comments, notifications and billing. Public forms
        (contact, newsletter, sign-up) are protected by Cloudflare Turnstile
        verified server-side, every sensitive route is rate-limited, CORS is
        locked to an explicit allowlist, and a global exception handler keeps
        internal errors from leaking to clients. Contact replies, newsletter
        mail and notifications go out through Brevo, with a direct SMTP
        fallback.
      </p>

      <h2 id="s8">8. Search and discoverability</h2>
      <p>
        A client-rendered SPA is invisible to most crawlers by default, so a
        large part of the work happened at the edge. Vercel middleware returns
        genuine 404 status codes for unknown paths instead of soft 404s, and
        injects titles, descriptions and JSON-LD structured data (Article for
        posts, Service for service pages, Organization and WebSite site-wide)
        into the HTML before it leaves the edge, without exposing
        subscriber-only bodies. The site also publishes machine-readable entry
        points for AI agents &mdash; link headers, an API catalog and OpenAPI
        description &mdash; and explicitly welcomes AI crawlers, so the
        guides can be cited by AI assistants as well as ranked by search
        engines.
      </p>

      <h2 id="s9">9. Delivery and maintenance</h2>
      <p>
        The project was delivered as a long series of small, reviewed pull
        requests &mdash; more than three hundred so far &mdash; each deployed
        automatically to Vercel and Railway. Since launch I have kept it moving
        with seasonal touches, a branded 404 page, and a recurring SEO routine
        that tracks rankings, fixes technical issues and maintains a keyword
        map for the destinations Britt covers.
      </p>

      <p className="colophon">
        Facts on this page are drawn from the project&rsquo;s source code and
        documentation. The website is live at{' '}
        <a href={SITE}>letslivewhimsically.com</a>. Typeset with{' '}
        <a href="https://github.com/vincentdoerig/latex-css">LaTeX.css</a>.
      </p>
    </article>
  );
};

export default WhimsicallyBrittProject;
